import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import Table from "@cloudscape-design/components/table";
import Toggle from "@cloudscape-design/components/toggle";
import styles from "../styles/otpRouteEditor.module.scss";

const LOGIC_OPTIONS = ["AND", "OR"].map((value) => ({ value, label: value }));
const OPERATOR_OPTIONS = ["=", "!=", "contains", "not_contains", "starts_with", "ends_with", "regex", "IN", "NOT IN", "IS NULL", "IS NOT NULL", ">", "<", ">=", "<="].map((value) => ({ value, label: value.replace(/_/g, " ") }));
const FIELD_OPTIONS = [
	["phoneCountry", "Phone country"],
	["userCountry", "User country (IP)"],
	["uid", "User ID"],
	["number", "Phone number"],
	["type", "OTP scenario"],
	["ip", "IP address"],
	["os", "Operating system"],
	["userAgent", "User agent"],
	["screen", "Screen resolution"],
	["brand", "Device brand"],
	["model", "Device model"],
	["lang", "Language"],
	["currency", "Currency"],
	["refCode", "Referral code"],
].map(([value, label]) => ({ value, label }));
const SCOPE_OPTIONS = [
	{ value: "phone", label: "Phone number" },
	{ value: "ip", label: "IP address" },
	{ value: "uid", label: "User ID" },
];
const MESSAGE_TYPE_OPTIONS = [
	{ value: "default", label: "Current common template" },
	{ value: "code_only", label: "Code only" },
	{ value: "key", label: "Translation key" },
];
const option = (options, value) => options.find((item) => item.value === String(value)) || null;
const isNullOperator = (operator) => ["IS NULL", "IS NOT NULL"].includes(operator);
const makeId = () => (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const newCondition = () => ({ id: makeId(), field: "userCountry", operator: "=", value: "", logicalOperator: null });
const normalizeConditions = (conditions) =>
	(Array.isArray(conditions) ? conditions : []).map((condition, index) => ({
		id: condition.id || makeId(),
		field: condition.field || "phoneCountry",
		operator: condition.operator || "=",
		value: condition.value ?? "",
		logicalOperator: index ? condition.logicalOperator || "AND" : null,
	}));

export const copyRuleGroups = (groups) =>
	(Array.isArray(groups) ? groups : []).map((group, index) => ({
		id: group.id || makeId(),
		name: group.name === undefined ? `Group ${index + 1}` : group.name,
		enabled: group.enabled === undefined ? true : group.enabled,
		logicalOperator: index ? group.logicalOperator || "OR" : null,
		conditions: normalizeConditions(group.conditions),
	}));

export const normalizeRouting = (routing) => ({
	blockRules: copyRuleGroups(routing?.blockRules),
	cohorts: (routing?.cohorts || []).map((cohort) => ({
		id: cohort.id || makeId(),
		name: cohort.name || "",
		conditions: normalizeConditions(cohort.conditions),
		providers: (cohort.providers || []).map((provider) => ({
			providerId: Number(provider.providerId),
			enabled: provider.enabled !== false,
			availabilityRules: copyRuleGroups(provider.availabilityRules),
			...(provider.conditions?.length ? { conditions: normalizeConditions(provider.conditions) } : {}),
			limits: Array.isArray(provider.limits) ? provider.limits.map((limit) => ({ ...limit })) : [],
			...(provider.messageMode ? { messageMode: provider.messageMode } : {}),
			...(provider.messageByType && typeof provider.messageByType === "object" ? { messageByType: { ...provider.messageByType } } : {}),
		})),
	})),
});

export const getRoutingError = (routing, providers) => {
	const groupIds = new Set();
	const conditionIds = new Set();
	const checkConditions = (conditions, label) => {
		for (const condition of conditions) {
			if (conditionIds.has(condition.id)) return "Condition IDs must be unique in the route.";
			conditionIds.add(condition.id);
		}
		if (conditions.some((condition) => condition.field === "countryMatch")) return `Phone and IP country matching is no longer available. Replace or remove these conditions in ${label} before saving.`;
		if (conditions.some((condition) => !isNullOperator(condition.operator) && String(condition.value ?? "").trim() === "")) return `Complete the condition values in ${label}.`;
		return "";
	};
	const checkGroups = (groups, label) => {
		if (!Array.isArray(groups)) return `The ${label} groups must be an array.`;
		for (const [index, group] of groups.entries()) {
			if (!group || typeof group !== "object" || !group.id) return `Check the ${label} group structure.`;
			if (groupIds.has(group.id)) return "Group IDs must be unique in the route.";
			groupIds.add(group.id);
			if (typeof group.name !== "string" || !group.name.trim() || group.name.trim().length > 64) return `Give each ${label} group a name of 1–64 characters.`;
			if (typeof group.enabled !== "boolean") return `Each ${label} group must have an enabled or disabled status.`;
			if (index === 0 ? group.logicalOperator != null : !["AND", "OR"].includes(group.logicalOperator)) return `Check the ${label} group operators.`;
			if (!Array.isArray(group.conditions) || !group.conditions.length) return `Add at least one condition to each ${label} group, or remove the empty group.`;
			const error = checkConditions(group.conditions, `${label} groups`);
			if (error) return error;
		}
		return "";
	};
	const blockingError = checkGroups(routing.blockRules === undefined ? [] : routing.blockRules, "blocking");
	if (blockingError) return blockingError;
	for (const [index, cohort] of routing.cohorts.entries()) {
		if (!cohort.name.trim()) return `Give cohort ${index + 1} a name.`;
		if (!cohort.conditions.length && index !== routing.cohorts.length - 1) return `“${cohort.name}” matches everyone. Move it to the end or add audience conditions.`;
		const cohortError = checkConditions(cohort.conditions, `“${cohort.name}”`);
		if (cohortError) return cohortError;
		for (const provider of cohort.providers) {
			if (providers && !providers.some((item) => Number(item.id) === Number(provider.providerId))) return `Replace unavailable provider ${provider.providerId} in “${cohort.name}”. SMSPobeda is now split into RU and International connections.`;
			if (provider.conditions?.length) return `Review the legacy availability conditions for provider ${provider.providerId} in “${cohort.name}” before saving.`;
			const availabilityError = checkGroups(provider.availabilityRules === undefined ? [] : provider.availabilityRules, "availability");
			if (availabilityError) return availabilityError;
			if (provider.limits.some((limit) => !Number.isInteger(limit.max) || limit.max < 1 || limit.max > 100000 || !Number.isInteger(limit.windowSeconds) || limit.windowSeconds < 1 || limit.windowSeconds > 2592000)) return `Check the limits in “${cohort.name}”: 1–100,000 attempts, with a window of 1–2,592,000 seconds.`;
			if (provider.messageByType !== undefined) {
				if (!provider.messageByType || typeof provider.messageByType !== "object" || Array.isArray(provider.messageByType)) return `Check the message settings in “${cohort.name}”.`;
				for (const [type, value] of Object.entries(provider.messageByType)) {
					if (!["confirm", "recovery"].includes(type) || typeof value !== "string") return `Check the message settings in “${cohort.name}”.`;
					const key = value.trim();
					if (key && key !== "{{code}}" && !/^[A-Za-z0-9_.-]{1,128}$/.test(key)) return `Check the ${type} template key in “${cohort.name}”.`;
				}
			}
		}
	}
	return "";
};

function ConditionsEditor({ conditions, countries, onChange, draft, onDraftChange }) {
	const blank = React.useRef({ ...newCondition(), field: "", logicalOperator: "AND" });
	const condition = draft || blank.current;
	const editing = Boolean(condition.editingId);
	const showLogic = editing ? conditions.findIndex((item) => item.id === condition.editingId) > 0 : conditions.length > 0;
	const update = (patch) => onDraftChange({ ...condition, ...patch });
	const commit = (items) => onChange(normalizeConditions(items));
	const countryOptions = countries.map((country) => ({ value: country.code, label: country.label }));
	const countryField = ["userCountry", "phoneCountry"].includes(condition.field);
	const simpleComparison = ["=", "!="].includes(condition.operator);
	const complete = Boolean(option(FIELD_OPTIONS, condition.field)) && (isNullOperator(condition.operator) || String(condition.value ?? "").trim() !== "");
	const saveCondition = () => {
		if (!complete) return;
		const { editingId, ...next } = condition;
		commit(editing ? conditions.map((item) => (item.id === editingId ? next : item)) : [...conditions, { ...next, id: makeId() }]);
		onDraftChange(null);
	};
	const displayValue = (item) => {
		if (isNullOperator(item.operator)) return "—";
		if (typeof item.value === "boolean") return item.value ? "Yes" : "No";
		if (["phoneCountry", "userCountry"].includes(item.field) && ["=", "!="].includes(item.operator)) return option(countryOptions, item.value)?.label || String(item.value);
		return Array.isArray(item.value) ? item.value.join(", ") : String(item.value ?? "");
	};
	return (
		<Table
			variant="embedded"
			trackBy="id"
			items={conditions}
			empty="No conditions set. Add a condition below."
			columnDefinitions={[
				{ id: "logic", header: "Logic", cell: (item) => item.logicalOperator || "—", width: 80 },
				{ id: "field", header: "Field", cell: (item) => option(FIELD_OPTIONS, item.field)?.label || (item.field === "countryMatch" ? "Phone and IP countries match (removed)" : item.field) },
				{ id: "operator", header: "Operator", cell: (item) => option(OPERATOR_OPTIONS, item.operator)?.label || item.operator },
				{ id: "value", header: "Value", cell: (item) => <span className={styles.conditionValue}>{displayValue(item)}</span> },
				{
					id: "actions",
					header: "Control",
					width: 110,
					cell: (item) => (
						<div className={styles.rowActions}>
							<Button variant="icon" iconName="edit" ariaLabel={`Edit condition ${conditions.indexOf(item) + 1}`} disabled={Boolean(draft) && condition.editingId !== item.id} onClick={() => onDraftChange({ ...item, editingId: item.id })} />
							<Button
								variant="icon"
								iconName="delete-marker"
								ariaLabel={`Remove condition ${conditions.indexOf(item) + 1}`}
								onClick={() => {
									commit(conditions.filter((entry) => entry.id !== item.id));
									if (condition.editingId === item.id) onDraftChange(null);
								}}
							/>
						</div>
					),
				},
			]}
			footer={
				<SpaceBetween size="s">
					<Box fontWeight="bold">{editing ? "Edit condition" : "Add condition"}</Box>
					<div className={styles.conditionComposer}>
						<FormField label="Logic">
							<Select ariaLabel="Condition logic" disabled={!showLogic} options={LOGIC_OPTIONS} selectedOption={showLogic ? option(LOGIC_OPTIONS, condition.logicalOperator || "AND") : { value: "", label: "WHEN" }} onChange={({ detail }) => update({ logicalOperator: detail.selectedOption.value })} />
						</FormField>
						<FormField label="Field">
							<Select ariaLabel="Condition field" filteringType="auto" placeholder="Select field" options={FIELD_OPTIONS} selectedOption={option(FIELD_OPTIONS, condition.field)} onChange={({ detail }) => update({ field: detail.selectedOption.value, value: "" })} />
						</FormField>
						<FormField label="Operator">
							<Select ariaLabel="Condition operator" options={OPERATOR_OPTIONS} selectedOption={option(OPERATOR_OPTIONS, condition.operator)} onChange={({ detail }) => update({ operator: detail.selectedOption.value, value: isNullOperator(detail.selectedOption.value) ? "" : condition.value })} />
						</FormField>
						<FormField label="Value">
							{isNullOperator(condition.operator) ? (
								<Input disabled value="Not required" />
							) : countryField && simpleComparison ? (
								<Select ariaLabel="Condition value" filteringType="auto" placeholder="Choose country" options={countryOptions} selectedOption={option(countryOptions, condition.value) || (condition.value ? { value: String(condition.value), label: String(condition.value) } : null)} onChange={({ detail }) => update({ value: detail.selectedOption.value })} />
							) : (
								<Input ariaLabel="Condition value" value={Array.isArray(condition.value) ? condition.value.join(", ") : String(condition.value ?? "")} placeholder={["IN", "NOT IN"].includes(condition.operator) ? "Comma-separated values" : "Enter value"} onChange={({ detail }) => update({ value: detail.value })} />
							)}
						</FormField>
					</div>
					<SpaceBetween direction="horizontal" size="xs">
						<Button iconName={editing ? "check" : "add-plus"} disabled={!complete} onClick={saveCondition}>
							{editing ? "Update condition" : "Add condition"}
						</Button>
						{draft ? (
							<Button variant="link" onClick={() => onDraftChange(null)}>
								Cancel condition
							</Button>
						) : null}
					</SpaceBetween>
				</SpaceBetween>
			}
		/>
	);
}

export function RuleGroupsEditor({ groups, countries, onChange, conditionDrafts, onDraftChange, availability = false }) {
	const commit = (items) => onChange(copyRuleGroups(items));
	const activeCount = groups.filter((group) => group.enabled).length;
	const firstActive = groups.findIndex((group) => group.enabled);
	const title = availability ? "Availability conditions" : "Blocking conditions";
	return (
		<SpaceBetween size="l">
			<Header
				variant="h3"
				description={availability ? "Matching active groups allow this provider. Otherwise the next provider is tried. Groups are combined from top to bottom." : "Matching active groups block the entire country route before any provider is called. These rules apply to all audiences and are checked again on manual switching. Groups are combined from top to bottom."}
				actions={
					<Button variant="primary" iconName="add-plus" onClick={() => commit([...groups, { id: makeId(), name: `Group ${groups.length + 1}`, enabled: true, logicalOperator: "OR", conditions: [] }])}>
						Add group
					</Button>
				}>
				{title} ({activeCount} active / {groups.length})
			</Header>
			{!activeCount ? <Box>{availability ? "No active availability groups. This provider is available to everyone." : "No active blocking groups. Conditions do not block the country cascade."}</Box> : null}
			{groups.map((group, index) => (
				<section key={group.id} aria-label={`${title} group ${index + 1}`}>
					<Container
						header={
							<Header
								variant="h3"
								actions={
									<SpaceBetween direction="horizontal" size="s">
										<Toggle checked={group.enabled} onChange={({ detail }) => commit(groups.map((item) => item.id === group.id ? { ...item, enabled: detail.checked } : item))}>
											{group.enabled ? "Enabled" : "Disabled"}
										</Toggle>
										<Button
											variant="icon"
											iconName="delete-marker"
											ariaLabel={`Remove group ${index + 1}`}
											onClick={() => {
												onDraftChange(group.id, null);
												commit(groups.filter((item) => item.id !== group.id));
											}}
										/>
									</SpaceBetween>
								}>
								{group.name || `Group ${index + 1}`}
							</Header>
						}>
						<SpaceBetween size="m">
							<FormField label="Group name">
								<Input ariaLabel={`Group ${index + 1} name`} value={group.name} onChange={({ detail }) => commit(groups.map((item) => item.id === group.id ? { ...item, name: detail.value } : item))} />
							</FormField>
							<div className={styles.groupOperator}>
								<FormField label="Group operator" description={index === firstActive ? "The first active group starts the expression. Disabled groups are ignored." : "Combine with the result of previous active groups."}>
									<Select ariaLabel={`Group ${index + 1} operator`} disabled={!index || index === firstActive} options={LOGIC_OPTIONS} selectedOption={index && index !== firstActive ? option(LOGIC_OPTIONS, group.logicalOperator) : { value: "", label: "WHEN" }} onChange={({ detail }) => commit(groups.map((item) => (item.id === group.id ? { ...item, logicalOperator: detail.selectedOption.value } : item)))} />
								</FormField>
							</div>
							<ConditionsEditor conditions={group.conditions} countries={countries} draft={conditionDrafts[group.id]} onDraftChange={(value) => onDraftChange(group.id, value)} onChange={(conditions) => commit(groups.map((item) => (item.id === group.id ? { ...item, conditions } : item)))} />
						</SpaceBetween>
					</Container>
				</section>
			))}
		</SpaceBetween>
	);
}

function LimitsEditor({ limits, onChange }) {
	const update = (index, patch) => onChange(limits.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
	return (
		<SpaceBetween size="m">
			<Box color="text-body-secondary">A reached limit stops the entire cascade. User ID limits do not apply to anonymous users.</Box>
			{!limits.length ? <Box>No provider-specific limits.</Box> : null}
			{limits.map((limit, index) => (
				<div className={styles.limit} key={index}>
					<FormField label="Count attempts per">
						<Select options={SCOPE_OPTIONS} selectedOption={option(SCOPE_OPTIONS, limit.scope)} onChange={({ detail }) => update(index, { scope: detail.selectedOption.value })} />
					</FormField>
					<FormField label="Maximum attempts">
						<Input type="number" value={String(limit.max ?? "")} onChange={({ detail }) => update(index, { max: Number(detail.value) })} />
					</FormField>
					<FormField label="Time window (seconds)">
						<Input type="number" value={String(limit.windowSeconds ?? "")} onChange={({ detail }) => update(index, { windowSeconds: Number(detail.value) })} />
					</FormField>
					<Button variant="icon" iconName="remove" ariaLabel={`Remove limit ${index + 1}`} onClick={() => onChange(limits.filter((_, itemIndex) => itemIndex !== index))} />
				</div>
			))}
			<Button iconName="add-plus" onClick={() => onChange([...limits, { scope: "phone", windowSeconds: 3600, max: 3 }])}>
				Add limit
			</Button>
		</SpaceBetween>
	);
}

export function ProviderSettingsModal({ step, provider, countries, onApply, onDismiss }) {
	const [draft, setDraft] = React.useState(() => {
		const { blockRules, ...settings } = step;
		return { ...settings, availabilityRules: copyRuleGroups(step.availabilityRules), limits: step.limits.map((limit) => ({ ...limit })) };
	});
	const [error, setError] = React.useState("");
	const [tab, setTab] = React.useState("availability");
	const [conditionDrafts, setConditionDrafts] = React.useState({});
	const messageValue = (type, source = draft) => {
		if (source.messageByType && Object.prototype.hasOwnProperty.call(source.messageByType, type)) return String(source.messageByType[type] ?? "");
		return source.messageMode === "code_only" ? "{{code}}" : "";
	};
	const modeForValue = (value) => (value === "{{code}}" ? "code_only" : value ? "key" : "default");
	const [messageModes, setMessageModes] = React.useState(() => ({ confirm: modeForValue(messageValue("confirm", step)), recovery: modeForValue(messageValue("recovery", step)) }));
	const update = (patch) => {
		setDraft((previous) => ({ ...previous, ...patch }));
		setError("");
	};
	const apply = () => {
		if (Object.values(conditionDrafts).some(Boolean)) {
			setError("Add, update or cancel the unfinished condition before applying provider settings.");
			setTab("availability");
			return;
		}
		const validationError = getRoutingError({ cohorts: [{ id: "provider", name: provider?.name || "Provider", conditions: [], providers: [draft] }] });
		if (validationError) {
			setError(validationError);
			return;
		}
		onApply(draft);
	};
	const reviewLegacyConditions = (convert) => {
		const { conditions, ...next } = draft;
		setDraft({ ...next, availabilityRules: convert ? copyRuleGroups([...draft.availabilityRules, { id: makeId(), conditions }]) : draft.availabilityRules });
		setError("");
	};
	const setTypeMessage = (type, value, mode = modeForValue(value)) => {
		const messageByType = {
			confirm: messageValue("confirm"),
			recovery: messageValue("recovery"),
			[type]: value,
		};
		setMessageModes((previous) => ({ ...previous, [type]: mode }));
		update({ messageByType, messageMode: Object.values(messageByType).every((message) => message === "{{code}}") ? "code_only" : "template" });
	};
	const renderTypeMessage = (type, label, commonKey) => {
		const mode = messageModes[type];
		const value = messageValue(type);
		return (
			<SpaceBetween size="s">
				<FormField label={label} description={`The current common template uses sms.${commonKey}.`}>
					<Select
						options={MESSAGE_TYPE_OPTIONS}
						selectedOption={option(MESSAGE_TYPE_OPTIONS, mode)}
						onChange={({ detail }) => {
							const nextMode = detail.selectedOption.value;
							setTypeMessage(type, nextMode === "code_only" ? "{{code}}" : nextMode === "default" ? "" : mode === "key" ? value : "", nextMode);
						}}
					/>
				</FormField>
				{mode === "key" ? (
					<FormField label="Translation key" description="Key from the sms section. A missing translation uses the common template.">
						<Input
							value={value}
							placeholder={commonKey}
							onChange={({ detail }) => setTypeMessage(type, detail.value, "key")}
						/>
					</FormField>
				) : null}
			</SpaceBetween>
		);
	};
	return (
		<Modal
			visible
			size="large"
			header={provider?.name || "Provider settings"}
			onDismiss={onDismiss}
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button onClick={onDismiss}>Cancel</Button>
						<Button variant="primary" onClick={apply}>
							Apply
						</Button>
					</SpaceBetween>
				</Box>
			}>
			<SpaceBetween size="m">
				{error ? <Alert type="error">{error}</Alert> : null}
				<Box color="text-body-secondary">Settings apply to this provider in this route. Save the country route to publish your changes.</Box>
				<Toggle checked={draft.enabled} onChange={({ detail }) => update({ enabled: detail.checked })}>
					{draft.enabled ? "Provider enabled" : "Provider disabled"}
				</Toggle>
				<Box color="text-body-secondary">Manual fallback is configured in the Providers tab and applies after this provider in all countries and cohorts. API errors still continue to the next provider automatically.</Box>
				{provider?.driver === "aws" ? <Box color="text-body-secondary">AWS SNS uses server-side AWS SDK credentials. No database keys are required; credentials must be available in the server environment.</Box> : provider?.usesBackendConfig ? <Alert type="warning">Configure credentials in the Providers tab before enabling this provider.</Alert> : null}
				<Tabs
					activeTabId={tab}
					onChange={({ detail }) => setTab(detail.activeTabId)}
					tabs={[
						{
							id: "availability",
							label: `Availability conditions (${draft.availabilityRules.filter((group) => group.enabled).length}/${draft.availabilityRules.length})`,
							content: (
								<SpaceBetween size="m">
									{draft.conditions?.length ? (
										<Alert type="warning" header="Review legacy availability conditions">
											<SpaceBetween size="s">
												<Box>These {draft.conditions.length} saved conditions previously allowed this provider. Move them to an availability group to preserve their meaning, or discard them.</Box>
												<SpaceBetween direction="horizontal" size="xs">
													<Button onClick={() => reviewLegacyConditions(true)}>Move to availability group</Button>
													<Button onClick={() => reviewLegacyConditions(false)}>Discard legacy conditions</Button>
												</SpaceBetween>
											</SpaceBetween>
										</Alert>
									) : null}
									<RuleGroupsEditor
										availability
										groups={draft.availabilityRules}
										countries={countries}
										conditionDrafts={conditionDrafts}
										onDraftChange={(id, value) => {
											setConditionDrafts((previous) => ({ ...previous, [id]: value }));
											setError("");
										}}
										onChange={(availabilityRules) => update({ availabilityRules })}
									/>
								</SpaceBetween>
							),
						},
						{ id: "limits", label: `Limits (${draft.limits.length})`, content: <LimitsEditor limits={draft.limits} onChange={(limits) => update({ limits })} /> },
						...(!["telegram", "sigma_push"].includes(provider?.driver)
							? [
									{
										id: "message",
										label: "Message",
										content: (
											<SpaceBetween size="l">
												{renderTypeMessage("confirm", "Registration / confirmation message", "signUpTemplate")}
												{renderTypeMessage("recovery", "Recovery message", "recoveryTemplate")}
											</SpaceBetween>
										),
									},
							  ]
							: []),
					]}
				/>
			</SpaceBetween>
		</Modal>
	);
}

export default function OtpRouteEditor({ country, lockedCountry, routing, countries, providers, routes, fallbackDelay, onCountryChange, onChange, onDialogChange, onConditionDraftChange }) {
	const [tab, setTab] = React.useState("cascade");
	const [conditionDrafts, setConditionDrafts] = React.useState({});
	const [selectedCohortId, setSelectedCohortId] = React.useState(routing.cohorts[0]?.id);
	const [providerDraft, setProviderDraft] = React.useState(null);
	const [pendingProvider, setPendingProvider] = React.useState(null);
	const [dragPreview, setDragPreview] = React.useState(null);
	const [announcement, setAnnouncement] = React.useState("");
	const dragState = React.useRef(null);
	const providerList = React.useRef(null);
	const previousPositions = React.useRef(null);
	const sortAnimations = React.useRef([]);
	const defaultCohort = React.useRef({ id: makeId(), name: "Default", conditions: [], providers: [] });
	const cohort = routing.cohorts.find((item) => item.id === selectedCohortId) || routing.cohorts[0] || defaultCohort.current;
	const steps = cohort.providers;
	const countryOptions = [{ value: "DEFAULT", label: "DEFAULT — all other countries" }, ...countries.map((item) => ({ value: item.code, label: item.label }))].filter((item) => item.value === country?.value || !routes.some((route) => route.country === item.value));
	const available = providers.filter((provider) => !steps.some((step) => Number(step.providerId) === Number(provider.id)));
	const providerOptions = available.map((provider) => ({ value: String(provider.id), label: provider.name, description: provider.driverLabel }));
	const updateSteps = (next) => onChange({ ...routing, cohorts: routing.cohorts.length ? routing.cohorts.map((item) => (item.id === cohort.id ? { ...item, providers: next } : item)) : [{ ...cohort, providers: next }] });
	const providerName = (id) => providers.find((provider) => Number(provider.id) === Number(id))?.name || `Provider ${id}`;
	const capturePositions = () => {
		previousPositions.current = new Map(Array.from(providerList.current?.children || [], (element) => [Number(element.dataset.otpProvider), element.getBoundingClientRect().top]));
	};
	React.useLayoutEffect(() => {
		const positions = previousPositions.current;
		if (!positions) return;
		previousPositions.current = null;
		sortAnimations.current.forEach((animation) => animation.cancel());
		sortAnimations.current = [];
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		for (const element of providerList.current?.children || []) {
			const previousTop = positions.get(Number(element.dataset.otpProvider));
			const offset = previousTop - element.getBoundingClientRect().top;
			if (previousTop === undefined || Math.abs(offset) < 1 || !element.animate) continue;
			sortAnimations.current.push(element.animate([{ transform: `translateY(${offset}px)` }, { transform: "translateY(0)" }], { duration: 220, easing: "cubic-bezier(0.2, 0, 0, 1)" }));
		}
	});
	React.useEffect(() => () => sortAnimations.current.forEach((animation) => animation.cancel()), []);
	const moveProvider = (fromId, toId) => {
		const from = steps.findIndex((step) => step.providerId === fromId);
		const to = steps.findIndex((step) => step.providerId === toId);
		if (from < 0 || to < 0 || from === to) return;
		if (!previousPositions.current) capturePositions();
		const next = steps.slice();
		next.splice(to, 0, next.splice(from, 1)[0]);
		updateSteps(next);
		setAnnouncement(`${providerName(fromId)} moved to position ${to + 1}.`);
	};
	const finishDrag = () => {
		if (dragState.current?.active) capturePositions();
		dragState.current = null;
		setDragPreview(null);
	};
	const movePointer = (event) => {
		const drag = dragState.current;
		if (!drag || (Math.abs(event.clientY - drag.startY) < 5 && !drag.active)) return;
		drag.active = true;
		const listRect = providerList.current.getBoundingClientRect();
		const source = drag.rows.find((row) => row.id === drag.providerId);
		const last = drag.rows[drag.rows.length - 1];
		const distance = event.clientY - drag.startY + drag.listTop - listRect.top;
		const offset = Math.max(-source.top, Math.min(distance, last.top + last.height - source.height - source.top));
		const reordered = drag.rows.filter((row) => row.id !== source.id);
		let targetIndex = 0;
		let targetTop = 0;
		let closestDistance = Infinity;
		for (let index = 0; index <= reordered.length; index++) {
			const distanceToSlot = Math.abs(source.top + offset - targetTop);
			if (distanceToSlot < closestDistance) {
				closestDistance = distanceToSlot;
				targetIndex = index;
			}
			if (reordered[index]) targetTop += reordered[index].height + drag.gap;
		}
		drag.targetId = drag.rows[targetIndex].id;
		reordered.splice(targetIndex, 0, source);
		let top = 0;
		const offsets = {};
		for (const row of reordered) {
			offsets[row.id] = row.id === source.id ? offset : top - row.top;
			top += row.height + drag.gap;
		}
		setDragPreview({ id: source.id, offsets });
	};
	const addProvider = () => {
		const provider = available.find((item) => String(item.id) === pendingProvider?.value);
		if (!provider) return;
		updateSteps([...steps, { providerId: Number(provider.id), enabled: true, availabilityRules: [], limits: [], ...(["telegram", "sigma_push"].includes(provider.driver) ? {} : { messageMode: "template" }) }]);
		setPendingProvider(null);
		setAnnouncement(`${provider.name} added to the cascade.`);
	};
	const closeSettings = () => {
		setProviderDraft(null);
		onDialogChange(false);
	};
	const hasSavedAudiences = routing.cohorts.length > 1 || Boolean(cohort.conditions.length);
	return (
		<SpaceBetween size="l">
			<FormField label="Country">
				<Select filteringType="auto" disabled={lockedCountry} selectedOption={country} options={countryOptions} placeholder="Choose country" onChange={({ detail }) => onCountryChange(detail.selectedOption)} />
			</FormField>
			<Tabs
				activeTabId={tab}
				onChange={({ detail }) => {
					finishDrag();
					setTab(detail.activeTabId);
				}}
				tabs={[
					{
						id: "cascade",
						label: "Cascade",
						content: (
							<SpaceBetween size="l">
								{hasSavedAudiences ? (
									<FormField label="Saved audience" description="This route already has audience-specific cascades. Changes below apply only to the selected audience.">
										<Select
											selectedOption={{ value: cohort.id, label: cohort.name }}
											options={routing.cohorts.map((item) => ({ value: item.id, label: item.name, description: item.conditions.length ? `${item.conditions.length} conditions` : "Fallback" }))}
											onChange={({ detail }) => {
												setSelectedCohortId(detail.selectedOption.value);
												setPendingProvider(null);
												finishDrag();
											}}
										/>
									</FormField>
								) : null}
								<div>
									<Header variant="h3" description="Drag to change the order. Click a provider to configure its availability groups and limits.">
										Providers
									</Header>
									{steps.some((step) => !providers.some((provider) => Number(provider.id) === Number(step.providerId))) && (
										<Alert type="warning" header="Provider replacement required">
											This cascade contains an unavailable provider. Replace the old SMSPobeda connection with SMSPobeda RU or SMSPobeda International and review its availability groups before saving.
										</Alert>
									)}
									<ol ref={providerList} className={styles.providers} aria-label="Provider delivery order">
										{steps.map((step, index) => (
											<li key={step.providerId} data-otp-provider={step.providerId} className={`${styles.providerRow} ${dragPreview ? styles.sorting : ""} ${dragPreview?.id === step.providerId ? styles.dragging : ""}`} style={dragPreview ? { transform: `translateY(${dragPreview.offsets[step.providerId] || 0}px)` } : undefined}>
												<button
													type="button"
													className={styles.dragHandle}
													aria-label={`Reorder ${providerName(step.providerId)}. Use up and down arrows.`}
													onPointerDown={(event) => {
														if (event.button !== 0) return;
														sortAnimations.current.forEach((animation) => animation.cancel());
														const listTop = providerList.current.getBoundingClientRect().top;
														const rows = Array.from(providerList.current.children, (element) => {
															const rect = element.getBoundingClientRect();
															return { id: Number(element.dataset.otpProvider), top: rect.top - listTop, height: rect.height };
														});
														const gap = Number.parseFloat(window.getComputedStyle(providerList.current).rowGap) || 0;
														dragState.current = { providerId: step.providerId, startY: event.clientY, listTop, rows, gap, active: false, targetId: step.providerId };
														event.currentTarget.setPointerCapture(event.pointerId);
													}}
													onPointerMove={movePointer}
													onPointerUp={() => {
														const drag = dragState.current;
														finishDrag();
														if (drag?.active) moveProvider(drag.providerId, drag.targetId);
													}}
													onPointerCancel={finishDrag}
													onLostPointerCapture={finishDrag}
													onKeyDown={(event) => {
														if (event.key === "Escape" && dragState.current) {
															event.preventDefault();
															event.stopPropagation();
															finishDrag();
															return;
														}
														if (dragState.current) return;
														if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;
														event.preventDefault();
														const target = index + (event.key === "ArrowUp" ? -1 : 1);
														if (steps[target]) moveProvider(step.providerId, steps[target].providerId);
													}}>
													<span aria-hidden="true">⠿</span>
												</button>
												<span className={styles.order}>{index + 1}</span>
												<button
													type="button"
													className={styles.providerButton}
													aria-label={`Configure ${providerName(step.providerId)}`}
													onClick={() => {
														setProviderDraft(step);
														onDialogChange(true);
													}}>
													<strong>{providerName(step.providerId)}</strong>
													<span className={styles.hint}>{step.enabled ? (step.conditions?.length ? "Legacy conditions need review" : `${step.availabilityRules.filter((group) => group.enabled).length} availability · ${step.limits.length} limits`) : "Disabled"}</span>
												</button>
												<div className={styles.rowActions}>
													<Button variant="icon" iconName="angle-up" ariaLabel={`Move ${providerName(step.providerId)} up`} disabled={!index} onClick={() => moveProvider(step.providerId, steps[index - 1].providerId)} />
													<Button variant="icon" iconName="angle-down" ariaLabel={`Move ${providerName(step.providerId)} down`} disabled={index === steps.length - 1} onClick={() => moveProvider(step.providerId, steps[index + 1].providerId)} />
													<Button variant="icon" iconName="remove" ariaLabel={`Remove ${providerName(step.providerId)}`} onClick={() => updateSteps(steps.filter((item) => item.providerId !== step.providerId))} />
												</div>
											</li>
										))}
									</ol>
									<div className={styles.addProvider}>
										<Select ariaLabel="Provider to add" placeholder={available.length ? "Choose provider" : "All providers added"} disabled={!available.length} options={providerOptions} selectedOption={pendingProvider} onChange={({ detail }) => setPendingProvider(detail.selectedOption)} />
										<Button iconName="add-plus" disabled={!pendingProvider} onClick={addProvider}>
											Add
										</Button>
									</div>
								</div>
								{!steps.some((step) => step.enabled) ? (
									<Alert type="warning" header="Silent suppression">
										No active providers. The user sees the code entry screen, but no message is sent.
									</Alert>
								) : (
									<Box color="text-body-secondary">API errors automatically continue to the next provider. After any provider succeeds, manual switching is available after {fallbackDelay}s when enabled for the current provider in the Providers tab and a next provider is available.</Box>
								)}
								<span role="status" className={styles.srOnly}>
									{announcement}
								</span>
							</SpaceBetween>
						),
					},
					{
						id: "blocking",
						label: `Blocking conditions (${routing.blockRules.filter((group) => group.enabled).length}/${routing.blockRules.length})`,
						content: (
							<RuleGroupsEditor
								groups={routing.blockRules}
								countries={countries}
								conditionDrafts={conditionDrafts}
								onDraftChange={(id, value) => {
									const next = { ...conditionDrafts, [id]: value };
									setConditionDrafts(next);
									onConditionDraftChange?.(Object.values(next).some(Boolean));
								}}
								onChange={(blockRules) => onChange({ ...routing, blockRules })}
							/>
						),
					},
				]}
			/>
			{Object.values(conditionDrafts).some(Boolean) ? <Box>Complete or cancel the unfinished blocking condition before saving the country route.</Box> : null}
			{providerDraft ? (
				<ProviderSettingsModal
					step={providerDraft}
					provider={providers.find((item) => Number(item.id) === Number(providerDraft.providerId))}
					countries={countries}
					onDismiss={closeSettings}
					onApply={(next) => {
						updateSteps(steps.map((item) => (item.providerId === next.providerId ? next : item)));
						closeSettings();
					}}
				/>
			) : null}
		</SpaceBetween>
	);
}
